import { Box } from '@mui/material'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import _ from 'lodash'
import moment from 'moment'
import * as React from 'react'
import { isDemoMode } from '../../api/acitonLogsAPI'
import { DayLogById, House, Log } from '../../types'
import { specialConfig } from '../../hooks/useLocalConfig'
import { isSoilHmdEnabledHouse } from '../../utils'

type Props = {
	isActive: boolean
	house: House
	log: Log | null
	dayLogs: DayLogById
}

// グループ見出し（左アクセントバー付き）。
const GroupTitle = ({ children }: React.PropsWithChildren<{}>) => (
	<Typography
		variant="overline"
		sx={{
			display: 'flex',
			alignItems: 'center',
			gap: 0.75,
			mb: 0.5,
			lineHeight: 1.6,
			fontWeight: 700,
			color: 'text.secondary',
			'&::before': {
				content: '""',
				display: 'inline-block',
				width: '3px',
				height: '12px',
				borderRadius: '2px',
				bgcolor: 'primary.main',
			},
		}}
	>
		{children}
	</Typography>
)

// センサー1項目（ラベル＋値、単位は小さく添える）。値が無いときは「-」。
const Stat = ({
	label,
	value,
	unit,
}: {
	label: string
	value: number | null | undefined
	unit?: string
}) => {
	const hasValue = typeof value === 'number' && isFinite(value)
	return (
		<Grid item xs={4}>
			<Typography
				variant="caption"
				sx={{ display: 'block', color: 'text.secondary' }}
			>
				{label}
			</Typography>
			<Typography
				variant="h6"
				sx={{ lineHeight: 1.25, fontVariantNumeric: 'tabular-nums' }}
			>
				{hasValue ? _.round(value, 1) : '-'}
				{hasValue && unit && (
					<Typography
						component="span"
						variant="caption"
						color="text.secondary"
						sx={{ ml: 0.3 }}
					>
						{unit}
					</Typography>
				)}
			</Typography>
		</Grid>
	)
}

const Device = (props: Props) => {
	const { log, dayLogs, house } = props

	const time = log
		? moment(log.createdAt).format('YYYY/MM/DD (dd) HH:mm')
		: '---'

	const config = specialConfig[house.id]
	const isSoil = isSoilHmdEnabledHouse(house.id)

	return (
		<Grid container>
			<Grid item xs={12} md={4}>
				{props.isActive || isDemoMode() ? (
					<Typography variant="h5" color="primary">
						稼働中
					</Typography>
				) : (
					<div>
						<Typography variant="h5">停止中</Typography>
						<Typography variant="subtitle1" color="error">
							5分以上ログが来ていません
						</Typography>
					</div>
				)}
				<Typography variant="h6">{time}</Typography>
				<Box sx={{ display: { md: 'none', lg: 'none' } }}>
					<hr />
				</Box>

				{log && (
					<Grid item md={5}>
						{Boolean(log.wat) ? (
							<Typography variant="h5" style={{ color: '#3344ff' }}>
								{'雨あり'}
							</Typography>
						) : (
							<Typography variant="h5" style={{ color: '#aaa' }}>
								{'雨なし'}
							</Typography>
						)}
					</Grid>
				)}
			</Grid>
			{log ? (
				<Grid item xs={12} md={8}>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
						<Box>
							<GroupTitle>環境</GroupTitle>
							<Grid container spacing={1}>
								<Stat label={config?.tmp || '気温'} value={log.tmp} unit="℃" />
								<Stat label="湿度" value={log.hmd} unit="％" />
								<Stat label="露点" value={log.dew} unit="℃" />
								<Stat label="飽差" value={log.vpd} />
								<Stat label="CO2" value={log.co2} unit="ppm" />
								<Stat label="日射量" value={log.lux} unit="W" />
							</Grid>
						</Box>

						<Box>
							<GroupTitle>移動平均気温</GroupTitle>
							<Grid container spacing={1}>
								<Stat label="24H" value={dayLogs.movingAvgTmp24} unit="℃" />
								<Stat label="48H" value={dayLogs.movingAvgTmp48} unit="℃" />
								<Stat label="72H" value={dayLogs.movingAvgTmp72} unit="℃" />
								<Stat label="積算気温" value={dayLogs.integrationTmp} />
							</Grid>
						</Box>

						<Box>
							<GroupTitle>
								{isSoil ? '土中センサー' : 'その他センサー'}
							</GroupTitle>
							<Grid container spacing={1}>
								<Stat
									label={config?.tmp2 || '外部温度1'}
									value={house.tmp2}
									unit="℃"
								/>
								<Stat
									label={config?.tmp3 || '外部温度2'}
									value={house.tmp3}
									unit="℃"
								/>
								{isSoil && (
									<Stat label="土中湿度" value={log.soilHmd} unit="％" />
								)}
							</Grid>
						</Box>
					</Box>
				</Grid>
			) : (
				<div>まだログがありません</div>
			)}
		</Grid>
	)
}

export default Device
